import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Article as ArticleIcon,
  PrivacyTip as ConsentIcon,
  QueryStats as MetricsIcon,
  RecordVoiceOver as AbhaIcon,
  Settings as SettingsIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { type ComponentType, useMemo } from 'react';
import type { SvgIconProps } from '@mui/material';
import useSWR from 'swr';
import { me, metrics } from '@/api/tenant';
import { MetricStat } from '@/components/MetricStat';
import { StatusPill } from '@/components/StatusPill';
import { PageShell } from '@/layout/PageShell';
import { useAuth } from '@/auth/AuthContext';
import { resolveWindow } from '@/utils/timeWindow';

/**
 * PR-C upgrade: the navigator card grid stays, plus a live stat strip
 * at the top sourced from `/api/abdm/tenant-metrics` over the last 7
 * days. The grid cards lose their PR labels — every PR-C destination
 * is now wired; PR-D destinations show "PR-D" so reviewers can still
 * see what's stubbed.
 */

interface SectionCard {
  title: string;
  description: string;
  to: string;
  icon: ComponentType<SvgIconProps>;
}

const sections: SectionCard[] = [
  { title: 'Metrics',      description: 'Per-tenant pass/fail counts, trend, and recent events.',         to: '/tenant/metrics',      icon: MetricsIcon },
  { title: 'My Audit',     description: 'Tenant-scoped event log and trace timeline.',                    to: '/tenant/audit',        icon: ArticleIcon },
  { title: 'My Webhooks',  description: 'Outbound deliveries to your registered receivers.',              to: '/tenant/webhooks',     icon: WebhookIcon },
  { title: 'Settings',     description: 'Session info, tenant context, and JWT claims.',                  to: '/tenant/settings',     icon: SettingsIcon },
  { title: 'ABHA',         description: 'Create + verify + share ABHA identities.',                       to: '/tenant/abha',         icon: AbhaIcon },
  { title: 'Care Context', description: 'Discover, link, and confirm care contexts.',                    to: '/tenant/care-context', icon: AccountTreeIcon },
  { title: 'Consent',      description: 'Request, list, and act on patient consent artefacts.',          to: '/tenant/consent',      icon: ConsentIcon },
];

export function TenantDashboard() {
  const theme = useTheme();
  const { claims } = useAuth();

  const win = useMemo(() => resolveWindow('7d'), []);
  const { data, error } = useSWR(
    ['tenant-dashboard-metrics', win.fromIso, win.toIso],
    () => metrics.read({ from: win.fromIso, to: win.toIso }),
  );
  const { data: tenantSelf } = useSWR('me-tenant', () => me.tenant());

  const totals = data ? {
    success: data.totalSuccess ?? 0,
    failure: data.totalFailure ?? 0,
    timeout: data.totalTimeout ?? 0,
  } : null;
  const passRate = totals
    ? totals.success + totals.failure + totals.timeout === 0
      ? null
      : (totals.success / (totals.success + totals.failure + totals.timeout)) * 100
    : null;

  return (
    <PageShell
      kicker="TENANT"
      title={`Hello, ${claims?.name ?? claims?.username ?? 'there'}.`}
      subtitle={
        claims?.tenantId
          ? `Working in ${claims.organizationName ?? claims.tenantId}. Last 7 days summary below; pick a section to drill in.`
          : 'Your tenant context will be wired in when you connect to live APIs.'
      }
      width="wide"
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          {tenantSelf && (
            <StatusPill tone={tenantSelf.environment === 'PRODUCTION' ? 'success' : 'info'}>
              {tenantSelf.environment}
              {tenantSelf.bridgeState ? ` · ${tenantSelf.bridgeState.replace(/_/g, ' ').toLowerCase()}` : ''}
            </StatusPill>
          )}
          {data?.source && (
            <StatusPill tone={data.source === 'live' ? 'info' : 'success'}>{data.source}</StatusPill>
          )}
        </Stack>
      }
    >
      {/* Live stat strip (last 7 days) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
        <MetricStat
          label="Pass rate · 7d"
          value={passRate === null ? (error ? '—' : '…') : `${passRate.toFixed(1)}%`}
          tone={passRate === null ? 'neutral' : passRate >= 99 ? 'success' : passRate >= 95 ? 'warning' : 'danger'}
          hint={totals ? `${totals.success.toLocaleString()} success / ${totals.failure.toLocaleString()} fail` : undefined}
        />
        <MetricStat label="Successful" value={totals?.success.toLocaleString() ?? '…'} tone="success" hint="last 7 days" />
        <MetricStat label="Failed"     value={totals?.failure.toLocaleString() ?? '…'} tone={(totals?.failure ?? 0) > 0 ? 'danger' : 'success'} hint="last 7 days" />
        <MetricStat label="Timeouts"   value={totals?.timeout.toLocaleString() ?? '…'} tone={(totals?.timeout ?? 0) > 0 ? 'warning' : 'success'} hint="last 7 days" />
      </Box>

      {/* Navigator card grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2.5,
        }}
      >
        {sections.map((section, idx) => (
          <Box
            key={section.to}
            component={motion.div}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.2, 0, 0, 1], delay: idx * 0.03 }}
          >
            <RouterLink to={section.to} style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  bgcolor: theme.palette.surface.card,
                  border: `1px solid ${theme.palette.border.subtle}`,
                  borderRadius: theme.radius.md + 'px',
                  p: 3,
                  transition: `border-color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}, box-shadow ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}, transform ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}`,
                  '&:hover': {
                    borderColor: theme.palette.accent.main,
                    boxShadow: theme.elevation.raised,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Stack spacing={1.75}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: theme.radius.sm + 'px',
                      bgcolor: theme.palette.accent.lighter,
                      color: theme.palette.accent.dark,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <section.icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h3" sx={{ fontWeight: 600, fontSize: 16 }}>
                        {section.title}
                      </Typography>
                      <Box
                        sx={{
                          fontSize: 9,
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          color: theme.palette.status.success,
                          bgcolor: theme.palette.status.successBg,
                          borderRadius: theme.radius.xs + 'px',
                          px: 0.75,
                          py: 0.25,
                        }}
                      >
                        LIVE
                      </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                      {section.description}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </RouterLink>
          </Box>
        ))}
      </Box>
    </PageShell>
  );
}
